/**
 * URL parsing helpers for the bootstrap flow.
 *
 * These read from `window.location` directly because `useBootstrapStore` is
 * a Zustand store living outside the React component tree — its actions
 * can't call React Router hooks. (Bootstrap itself runs inside a useEffect
 * after React mounts, so React Router IS available at call time, just not
 * from inside the store action.)
 *
 * A contributing factor: there are currently no declarative React Router
 * routes for graph views (`/:username/:contextName/edit` etc.) — the
 * <Routes> tree only handles app pages (apps, settings, ...) and everything
 * else falls through to <Home>, which does its own URL parsing. If those
 * routes existed, most of this file could be replaced with `useParams()`
 * inside the route components.
 *
 */

import { VIEW_TYPES, deriveViewTypeFromLocation } from '../../utils/viewType'
import {
    getStoredStopWords,
    getStoredPinnedNodes,
} from '../../utils/selectionStorage'

/**
 * Known routes that are NOT graph routes (i.e., these are app routes,
 * not /:username/:graph). These routes can never be "public" even if
 * there is no logged-in user.
 */
export const NON_GRAPH_ROUTES = [
    'apps',
    'import',
    'subscription',
    'upgrade',
    'settings',
    'api-access',
    'admin-panel',
    'switch',
    'logout',
    'login',
    'signup',
]

/**
 * Check if the current URL is on a graph-family route (/:username/*).
 * Returns false for app routes like /apps, /import, /settings, etc.
 *
 * Accepts single-segment URLs like `/:username/?showcontexts=1` — the
 * public top-synthesis URL form generated by `routes.js:showAllUrlPublic`.
 * The previous implementation required at least 2 path segments, which
 * excluded that form and broke anonymous visits to public synthesis views.
 */
export const isOnGraphRoute = () => {
    if (typeof window === 'undefined' || !window.location) return false
    const pathParts = window.location.pathname.split('/').filter(Boolean)

    // Must have at least 1 path segment (username)
    if (pathParts.length < 1) return false

    // First segment should NOT be a known non-graph route
    const firstSegment = pathParts[0].toLowerCase()
    if (NON_GRAPH_ROUTES.includes(firstSegment)) return false

    return true
}

/**
 * Determines if the current URL is viewing a public graph.
 * Can only be public if:
 * 1. We're on a graph route (/:username/:graphName)
 * 2. AND either not logged in OR viewing someone else's graph
 */
export const determineIsPublic = (loggedInUsername, urlUsername) => {
    if (!isOnGraphRoute()) return false
    return (
        !loggedInUsername ||
        (loggedInUsername && urlUsername !== loggedInUsername)
    )
}

/**
 * Extract username from URL path. Returns null if not on a graph route.
 */
export const getUsernameFromUrl = () => {
    if (typeof window === 'undefined' || !window.location) return null
    if (!isOnGraphRoute()) return null
    return decodeURIComponent(
        window.location.pathname.replace(/^\/([^\/]*).*$/, '$1')
    )
}

/**
 * Extract context name from URL path.
 */
export const getContextNameFromUrl = () => {
    if (typeof window === 'undefined' || !window.location) return null
    const pathParts = window.location.pathname.split('/').filter(Boolean)
    return pathParts.length > 1 ? decodeURIComponent(pathParts[1]) : null
}

/**
 * Custom graph-canvas background from the URL: `?bg_color=<hex>` (3- or
 * 6-digit, no `#`). Lets marketing embeds blend the canvas into the host
 * page (e.g. the landing hero). Invalid or absent → null (theme default).
 */
export const getBgColorFromUrl = () => {
    if (typeof window === 'undefined' || !window.location) return null
    const urlParams = new URLSearchParams(window.location.search)
    const hexValue = urlParams.get('bg_color')
    return /^[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?$/.test(hexValue || '')
        ? `#${hexValue}`
        : null
}

/**
 * Determine the view type from the current window.location.
 * Thin wrapper around the shared `deriveViewTypeFromLocation` helper —
 * see `views/react/utils/viewType.js` for the canonical decision logic.
 *
 * @param {string|null} loggedInUsername - The actually logged-in user's
 *     username (or null for anonymous). This must NOT be the URL owner's
 *     username — otherwise anonymous visits to /:someone-else would be
 *     misclassified as top synthesis for that user.
 */
export const getViewTypeFromUrl = (loggedInUsername = null) => {
    if (typeof window === 'undefined' || !window.location) {
        return VIEW_TYPES.CONTEXT
    }
    return deriveViewTypeFromLocation(window.location, loggedInUsername)
}

/**
 * Get addcontext parameters from URL.
 */
export const getAddContextsFromUrl = () => {
    if (typeof window === 'undefined' || !window.location) return []
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.getAll('addcontext')
}

// The split omits `+` on purpose: it's the canonical separator, but
// URLSearchParams has already decoded it to a space by the time we read here.
export const getStopWordsFromUrl = () => {
    if (typeof window === 'undefined' || !window.location) return []
    const urlParams = new URLSearchParams(window.location.search)
    const stopwordsParam = urlParams.get('stopwords')
    if (!stopwordsParam) return []
    return stopwordsParam.split(/[\s,]+/).filter(Boolean)
}

export const getKeywordsFromUrl = () => {
    if (typeof window === 'undefined' || !window.location) return []
    const urlParams = new URLSearchParams(window.location.search)
    const keywordsParam = urlParams.get('keywords')
    if (!keywordsParam) return []
    return keywordsParam.split(/[\s,]+/).filter(Boolean)
}

// useFilterStore (module-eval seed) and useBootstrapStore (graph load) must
// both call this — divergent URL-vs-storage gating would desync the pill UI
// from the graph. `has()` (not value-truthiness) so a cleared `?stopwords=`
// overrides stored selections instead of falling back to them.
export const resolveInitialStopWords = () => {
    if (typeof window === 'undefined' || !window.location) return []
    const params = new URLSearchParams(window.location.search)
    return params.has('stopwords')
        ? getStopWordsFromUrl()
        : getStoredStopWords()
}

/**
 * Resolve which graph layer to open on load from the URL.
 *
 * Primary param: `?graph_panel=<concepts|topics|gaps|trends>` — the new,
 * explicit way share/embed URLs preserve the active layer. Emitted by
 * `LeftMenuLogic.ejs:generateShareURL` from the current Zustand
 * `currentGraphMode`.
 *
 * Legacy fallback: the EJS app's `?panel=` referred to the *statements*
 * side panel, not the graph layer, but two of its values do correspond
 * to a graph-layer intent and are mapped accordingly:
 *   - `missing`, `insight` → `gaps`
 *   - `summary`            → `topics`
 *   - anything else        → ignored
 *
 * Returns one of `'concepts' | 'topics' | 'gaps' | 'trends'`, or `null`
 * if neither param is set — callers should treat `null` as "use the
 * default" (currently `'topics'` for context/synthesis views).
 */
const VALID_GRAPH_PANELS = ['concepts', 'topics', 'gaps', 'trends']
const LEGACY_PANEL_TO_GRAPH_PANEL = {
    missing: 'gaps',
    insight: 'gaps',
    summary: 'topics',
}

export const getGraphPanelFromUrl = () => {
    if (typeof window === 'undefined' || !window.location) return null
    const params = new URLSearchParams(window.location.search)

    const explicit = (params.get('graph_panel') || '').toLowerCase()
    if (VALID_GRAPH_PANELS.includes(explicit)) return explicit

    const legacy = (params.get('panel') || '').toLowerCase()
    if (LEGACY_PANEL_TO_GRAPH_PANEL[legacy]) {
        return LEGACY_PANEL_TO_GRAPH_PANEL[legacy]
    }

    return null
}

/**
 * Resolve the initial state of the graph's controls / analytics panel
 * from `?controls=contracted|expanded`. The graph package
 * (`@infranodus/infranodus-graph`) accepts the same value through its
 * `controls` React prop and its own `?controls=` iframe URL param — we
 * forward it via the React prop so it works for non-iframe share links
 * too. Returns `null` when the param is missing or unrecognised — callers
 * treat that as "let the graph use its own heuristic" (iframe / narrow
 * viewports → contracted; desktop → expanded).
 */
const VALID_CONTROLS_MODES = ['contracted', 'expanded']

export const getControlsFromUrl = () => {
    if (typeof window === 'undefined' || !window.location) return null
    const params = new URLSearchParams(window.location.search)
    const raw = (params.get('controls') || '').toLowerCase()
    return VALID_CONTROLS_MODES.includes(raw) ? raw : null
}

export const resolveInitialPinnedNodes = () => {
    if (typeof window === 'undefined' || !window.location) return []
    const params = new URLSearchParams(window.location.search)
    return params.has('keywords')
        ? getKeywordsFromUrl()
        : getStoredPinnedNodes()
}

/**
 * Derive the compareMode for the backend from URL params.
 * Returns null when not in compare mode (no addcontext).
 *
 * URL param  → backend compareMode
 * ?merge=1   → 'merge'
 * ?highlight / ?missing → 'difference'
 * ?interpret → 'merge'  (embedding uses the fully merged graph)
 * ?addcontext (plain) → 'intersection'
 */
export const getCompareModeFromUrl = addContexts => {
    if (!addContexts || addContexts.length === 0) return null
    if (typeof window === 'undefined' || !window.location) return 'intersection'
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('merge')) return 'merge'
    if (urlParams.get('highlight') || urlParams.get('missing'))
        return 'difference'
    if (urlParams.get('interpret')) return 'merge'
    return 'intersection'
}
